import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import BarcodeInput from 'components/field/BarcodeInput';
import Button from 'components/field/Button';
import CurrencyInput from 'components/field/CurrencyInput';
import EnumSelect from 'components/field/EnumSelect';
import IntegerInput from 'components/field/IntegerInput';
import StringInput from 'components/field/StringInput';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import Modal from 'components/global/Modal';
import ProductImage from 'components/global/ProductImage';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';
import { updateDynamicListsInPage } from 'components/list/dynamicListSlice';
import Table from 'components/table/Table';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useLocale from 'hooks/useLocale';
import { usePrompt } from 'hooks/usePrompt';

import ReasonConfigForm from 'pages/Settings/ReasonConfig/ReasonConfigForm';

import { ENUMS, ORDER_ACTIONS, SOUND } from 'services/consts';

import AppLogger from 'utils/AppLogger';

import { faFloppyDisk } from '@fortawesome/pro-solid-svg-icons';

import OrderStatuses from './OrderStatuses';

const ReturnConfirmation = NiceModal.create(({ selectedOrder }) => {
	const [returnReasonOptions, setReturnReasonOptions] = useState(null);
	const [isLoading, setIsLoading] = useState(true);
	const modal = useModal();
	const axiosPrivate = useAxiosPrivate();
	const dispatch = useDispatch();

	const { Currency } = useLocale();
	const { showError, showSuccess } = useAlert();
	const { t } = useTranslation();

	const handleCloseModal = () => {
		modal.remove();
	};

	const onBarcodeEnter = (value) => {
		const orderItems = watch('orderItems');
		const index = orderItems.findIndex((item) => item.barcode === value || item.sku === value);
		if (index !== -1) {
			const qtyToConfirm = (orderItems[index].qtyToConfirm ?? 0) + 1;
			if (qtyToConfirm > (orderItems[index].qtyOrdered ?? 0) - (orderItems[index].qtyReturned ?? 0)) {
				const errorSound = new Audio(SOUND.BARCODE_ERROR);
				errorSound.play();
				showError({
					message: 'qtyToBeReturnedAndQtyReturnedSumCannotBeGreaterThanQtyOrdered'
				});
				return;
			}
			const successSound = new Audio(SOUND.BARCODE_SUCCESS);
			successSound.play();
			setValue(`orderItems[${index}].qtyToConfirm`, qtyToConfirm, { shouldValidate: true, shouldDirty: true });
		} else {
			const errorSound = new Audio(SOUND.BARCODE_ERROR);
			errorSound.play();
		}
	};

	const schema = useMemo(
		() =>
			yup
				.object()
				.shape({
					returnReasonOptions: yup.string().nullable().required(),
					orderItems: yup.array().of(
						yup.object().shape({
							qtyToConfirm: yup
								.number()
								.nullable()
								.max(yup.ref('qtyOrdered') ?? 0 - (yup.ref('qtyReturned') ?? 0), () =>
									t('qtyToBeReturnedAndQtyReturnedSumCannotBeGreaterThanQtyOrdered')
								),
							returnReason: yup.string().nullable()
						})
					)
				})
				.test('chooseAtLeastOneProduct', t('chooseAtLeastOneProduct'), (value) => {
					if (value.orderItems.length) {
						return value.orderItems.some((item) => item.qtyToConfirm > 0);
					} else {
						return true;
					}
				})
				.required(),
		[]
	);

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: {
			orderItems: [
				{
					returnReason: null
				}
			]
		}
	});

	const {
		handleSubmit,
		setValue,
		watch,
		reset,
		formState: { isDirty, isSubmitting, isSubmitSuccessful }
	} = formMethods;

	usePrompt(isDirty && !isSubmitting && !isSubmitSuccessful);

	const onSubmit = async (data) => {
		if (returnReasonOptions !== data.returnReasonOptions) {
			await axiosPrivate.post(ORDER_ACTIONS.UPDATE_RETURN_REASON, {
				otoOrderIDs: [selectedOrder.ID],
				returnReasonOptions: data.returnReasonOptions
			});
		}
		await axiosPrivate
			.post(ORDER_ACTIONS.UPDATE_RETURN_CONFIRMATION, {
				ID: selectedOrder.ID,
				...data,
				currency: data.currency ?? Currency,
				refundedAmount: Number(data.refundedAmount),
				returnReason: data.returnReason ?? '',
				orderNumber: undefined
			})
			.then((response) => {
				if (response.status === 200) {
					showSuccess();
					dispatch(updateDynamicListsInPage());
					handleCloseModal();
				} else {
					AppLogger.error(response);
				}
			})
			.catch((error) => {
				AppLogger.error(error);
				showError({
					message: error?.response?.data?.code || error?.response?.data?.message
				});
			});
	};

	const fillForm = useCallback(async () => {
		setIsLoading(true);

		await axiosPrivate
			.post(ORDER_ACTIONS.GET_RETURN_CONFIRMATION, {
				ID: selectedOrder.ID
			})
			.then((response) => {
				const { data, status } = response;
				if (status === 200) {
					reset({
						orderItems: data.orderItems.map((item) => ({
							...item,
							qtyToConfirm: item.qtyToConfirm ?? 0,
							returnReason: item.returnReason ?? null
						})),
						refundedAmount: data.refundedAmount ?? null,
						returnReasonOptions: data.returnReasonOptions ?? null,
						returnReason: data.returnReason ?? null,
						currency: data.currency ?? Currency
					});
					setReturnReasonOptions(data.returnReasonOptions ?? null);
				} else {
					AppLogger.error(response);
				}
			})
			.catch((error) => {
				AppLogger.error(error);
				showError();
			});

		setIsLoading(false);
	}, [selectedOrder.ID]);

	useEffect(() => {
		if (selectedOrder.ID) {
			fillForm();
		}
	}, [selectedOrder.ID]);

	const defaultColSpan = {
		md: 6
	};

	const columns = useMemo(
		() => [
			{
				header: 'productName',
				renderCell: ({ row }) => (
					<ProductImage
						data={{
							name: row.original.name,
							imageURL: row.original.image
						}}
					/>
				),
				size: 150
			},
			{
				header: 'sku',
				accessorKey: 'sku',
				size: 100
			},
			{
				header: 'barcode',
				accessorKey: 'barcode',
				size: 100
			},
			{
				header: 'orderedQuantity',
				accessorKey: 'qtyOrdered',
				size: 100
			},
			{
				header: 'qtyReturnRequested',
				accessorKey: 'qtyReturnRequested',
				size: 100
			},
			{
				header: 'qtyToConfirm',
				renderCell: ({ row }) => (
					<IntegerInput
						value={watch(`orderItems[${row.index}].qtyToConfirm`)}
						setValue={(val) => {
							if (val > (row.original.qtyOrdered ?? 0) - (row.original.qtyReturned ?? 0)) {
								showError({
									message: 'qtyToBeReturnedAndQtyReturnedSumCannotBeGreaterThanQtyOrdered'
								});
								return;
							}
							setValue(`orderItems[${row.index}].qtyToConfirm`, val, {
								shouldValidate: true,
								shouldDirty: true
							});
						}}
					/>
				),
				size: 150
			},
			{
				header: 'returnedQuantity',
				renderCell: ({ row }) => <IntegerInput name={`orderItems[${row.index}].qtyReturned`} disabled />,
				size: 100
			},
			{
				header: 'returnReason',
				renderCell: ({ row }) => <StringInput name={`orderItems[${row.index}].returnReason`} />,
				size: 300
			}
		],
		[]
	);

	return (
		<Modal maxWidth="xx-large" fullwidth className="modal-returnConfirmation">
			<ModalTitle>
				<Typography variant="p1">returnConfirmation</Typography>
				<Typography variant="p1" fontWeight="500">
					&nbsp;({selectedOrder.orderID})
				</Typography>
			</ModalTitle>
			<ModalContent>
				<Form formMethods={formMethods}>
					<FormItem label="returnReason" required colSpan={defaultColSpan}>
						<EnumSelect
							name="returnReasonOptions"
							enumName={ENUMS.RETURN_REASON_CONFIG}
							placeholder="selectOne"
							value={watch('returnReasonOptions')}
							setValue={(value) => {
								setValue('returnReasonOptions', value, { shouldValidate: true, shouldDirty: true });
							}}
							firstItem={{
								action: () =>
									NiceModal.show(ReasonConfigForm, {
										setReasonConfig: (value) => {
											setValue(`returnReasonOptions`, value, {
												shouldValidate: true,
												shouldDirty: true
											});
										},
										reasonType: 'return'
									}),
								label: <Typography color="var(--blue)">addANewReason</Typography>
							}}
						/>
					</FormItem>
					<FormItem label="refundedAmount" isLoading={isLoading} colSpan={defaultColSpan}>
						<CurrencyInput
							disabled
							name="refundedAmount"
							value={watch('currency')}
							setValue={(value) => {
								setValue(`currency`, value, { shouldValidate: true, shouldDirty: true });
							}}
						/>
					</FormItem>
					<FormItem label="customerReturnComment" isLoading={isLoading}>
						<StringInput disabled name="returnReason" />
					</FormItem>
					<FormItem label="barcodeOrSKU" isLoading={isLoading} roleName="EnterprisePackage">
						<BarcodeInput onChange={onBarcodeEnter} />
					</FormItem>
					<FormItem>
						<Table columns={columns} data={watch('orderItems')} loading={isLoading} />
					</FormItem>
					<FormItem>
						<OrderStatuses ID={selectedOrder.ID} returnMode={true} />
					</FormItem>
				</Form>
			</ModalContent>
			<ModalActions>
				<Button label="cancel" onClick={handleCloseModal} variant="outline" />
				<Button
					onClick={handleSubmit(onSubmit, (errors) => {
						if (errors?.['']?.type === 'chooseAtLeastOneProduct') {
							showError({
								message: 'chooseAtLeastOneProduct'
							});
						}
						AppLogger.log(errors);
					})}
					icon={faFloppyDisk}
					loading={isSubmitting}
					label="save"
					variant="blue"
				/>
			</ModalActions>
		</Modal>
	);
});

export default ReturnConfirmation;
